import React, { useEffect, useState } from 'react'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import styled from 'styled-components/native'
import { NetworkStatus, useQuery } from '@apollo/client'

import Modal from '../../../components/common/Modal'
import { Flex } from '../../../components/FlexBox'
import { H4, Text } from '../../../components/common/Text'
import CheckBox from '../../../components/common/CheckBox'
import Dropdown from '../../../components/common/Dropdown'
import useContentTopicQuery from '../../../components/common/form/hooks/useContentTopicQuery'
import TextInput from '../../../ui-library/TextInput'
import ClearIcon from '../../../components/icon/ClearIcon'
import theme from '../../../constants/Theme'
import useDebounce from '../../../hooks/useDebounce'
import SmallButton from './SmallButton'
import DismissIcon from '../../../components/icon/DismissIcon'
import useTranslation from '../../../hooks/useTranslation'
import {
  CANDIDATE_PAGE_SIZE,
  contentFeedBoostCandidatesQuery,
  IContentFeedBoostCandidate,
  IContentFeedBoostCandidatesResponse
} from '../queries/getContentFeedBoostCandidates'
import { IContentFeedBoost, IContentFeedItem } from '../queries/getContentFeeds'
import { asBoost } from '../hooks/useFeedBoosts'
import ContentItemSummary from './ContentItemSummary'
import OpenInMemberAppLink from './OpenInMemberAppLink'
import { TableCol, TableRow } from './Table'

const keyOf = (itemType: string, itemId: string) => `${itemType}:${itemId}`

/** Stable identity, so the default prop does not re-seed state every render. */
const NO_TOPICS: string[] = []

/**
 * Anchors the picker as a full-height panel on the right rather than a centred
 * card. react-modal merges these over its own defaults, so the inset, border,
 * radius and padding it would otherwise apply all have to be cleared here.
 */
const PANEL_OVERLAY = {
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  justifyContent: 'flex-end',
  padding: 0
}

const PANEL_CONTENT = {
  position: 'static',
  display: 'flex',
  flexDirection: 'column',
  width: '50%',
  minWidth: 480,
  maxWidth: '100%',
  height: '100%',
  inset: 'auto',
  margin: 0,
  padding: 0,
  border: 'none',
  borderRadius: 0,
  background: 'transparent',
  overflow: 'hidden'
}

/** Square off the card and let it fill the panel instead of hugging content. */
const PANEL_BODY = {
  flex: 1,
  borderRadius: 0,
  padding: 0,
  minWidth: 0
}

/**
 * Sits over the right-hand end of the search field. Absolute rather than a
 * sibling column so the input keeps the full width of the panel; the input
 * carries matching right padding so text cannot run underneath it.
 */
const ClearButton = styled(TouchableOpacity)`
  position: absolute;
  right: 8px;
  top: 0;
  bottom: 0;
  justify-content: center;
  padding-left: 6px;
  padding-right: 6px;
`

interface SelectRowProps {
  item: IContentFeedItem | null
  checked: boolean
  disabled?: boolean
  right?: React.ReactNode
  onPress?: () => void
}

/**
 * One row, used for both the already-added entries and the pickable ones so the
 * two lay out identically.
 *
 * The flexShrink: 0 wrapper is load-bearing. These rows sit in a ScrollView
 * with a maxHeight, and without it every row is compressed below its content
 * height once the list overflows -- text from adjacent rows overlaps and the
 * fixed-height thumbnails spill into their neighbours.
 */
const SelectRow = ({
  item,
  checked,
  disabled = false,
  right,
  onPress
}: SelectRowProps) => (
  // Dim the whole row, not just the checkbox. With only the box faded, a row
  // barred by the selection limit looked identical to a selectable one.
  <View style={{ flexShrink: 0, opacity: disabled ? 0.5 : 1 }}>
    <TouchableOpacity onPress={onPress} disabled={!onPress || disabled}>
      <TableRow>
        <TableCol style={{ maxWidth: 44, minWidth: 44 }}>
          {/* Solid when checked: the library's default outlined tick is hard
              to tell from the empty box at this size. */}
          <CheckBox
            title=""
            checked={checked}
            disabled={disabled}
            onPress={onPress}
            checkedIcon="check-square"
            uncheckedIcon="square-o"
          />
        </TableCol>
        <TableCol style={{ minWidth: 260 }}>
          <ContentItemSummary item={item} />
        </TableCol>
        <TableCol style={{ maxWidth: 140, minWidth: 100 }}>{right}</TableCol>
        {/* Preview in the member app. Kept as its own column rather than folded
            into `right`, which the two callers fill differently. */}
        <TableCol style={{ maxWidth: 36, minWidth: 36 }}>
          <OpenInMemberAppLink item={item} />
        </TableCol>
      </TableRow>
    </TouchableOpacity>
  </View>
)

export interface SelectContentModalProps {
  open: boolean
  close: () => void
  contentFeedId: string
  /** The feed's own topics. Seeds the topic filter; may be empty. */
  feedTopicIds?: string[]
  /** Already curated. Shown as "Added" and not selectable. */
  currentBoosts: IContentFeedBoost[]
  /** 'replace' swaps one slot (single-select); 'add' appends (multi-select). */
  mode: 'replace' | 'add'
  /** Add mode: how many more fit before the cap. Ignored when replacing. */
  remainingSlots?: number
  /** Add mode: the feed's limits, stated up front rather than counted down. */
  maxItems?: number
  minItems?: number
  onConfirm: (picked: IContentFeedBoost[]) => void
  saving?: boolean
}

/**
 * Pill values map onto the query's ContentFeedItemType argument.
 *
 * `media` covers both audio and video, which is why its label reads
 * "Listen/Watch". Splitting them would need a new server argument: audio vs
 * video lives on HostMedia.type, not on ContentFeedItemType.
 */
const FORMATS: { key: string; types?: string[] }[] = [
  { key: 'all' },
  { key: 'media', types: ['media'] },
  { key: 'read', types: ['document', 'doclet'] }
]

/**
 * Picks content for a feed. Used by the hero slots on Content home and by the
 * content panel on a feed's page, which is why the caller supplies the mode
 * and does the writing -- this component only returns the chosen items.
 *
 * Search is Postgres full text over each item type's textTsVector, not
 * Typesense, using plainto_tsquery -- terms are ANDed and need not be adjacent,
 * but a word has to be complete before it matches.
 *
 * Candidates are restricted server-side to content posted to Across Groups;
 * there is no client-side switch for it, so the header simply states it.
 */
export const SelectContentModal = ({
  open,
  close,
  contentFeedId,
  feedTopicIds = NO_TOPICS,
  currentBoosts,
  mode,
  remainingSlots,
  maxItems,
  minItems,
  onConfirm,
  saving = false
}: SelectContentModalProps) => {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string[]>([])
  // Controlled rather than DebouncedTextInput, which owns its value internally
  // and so cannot be cleared from outside. The input stays responsive; only the
  // value the query reads is debounced.
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounce(searchInput, 300)
  const [format, setFormat] = useState('all')
  // Seeded from the feed rather than left empty. The server has always fallen
  // back to the feed's topics when none were sent, so a blank filter reading
  // "Any topic" described a list that was in fact already narrowed. Pre-filling
  // makes the control agree with the results, and clearing it now widens the
  // search for real -- which is why topicIds is sent even when empty.
  const [topicIds, setTopicIds] = useState<string[]>(feedTopicIds)

  const { data: topicData } = useContentTopicQuery()
  const topicOptions = topicData?.contentTopics ?? []

  const types = FORMATS.find(f => f.key === format)?.types

  const {
    data,
    loading,
    error,
    networkStatus,
    fetchMore
  } = useQuery<IContentFeedBoostCandidatesResponse>(
    contentFeedBoostCandidatesQuery,
    {
      variables: {
        contentFeedId,
        query: search || undefined,
        types,
        // Always sent, never elided: [] is the server's signal for "no topic
        // filter", and omitting it would silently reinstate the feed's topics.
        topicIds,
        offset: 0,
        size: CANDIDATE_PAGE_SIZE
      },
      skip: !open || !contentFeedId,
      // Filters change the whole result set, so show the spinner rather than
      // leaving the previous list on screen looking like a stale match.
      notifyOnNetworkStatusChange: true
    }
  )

  // Reset on close rather than on open, so the next open renders with the right
  // filter already in place -- seeding it on open would fire one request for the
  // stale filter before the effect could correct it.
  const feedTopicKey = feedTopicIds.join(',')
  useEffect(() => {
    if (!open) {
      setSelected([])
      setSearchInput('')
      setFormat('all')
      setTopicIds(feedTopicIds)
    }
    // feedTopicKey stands in for the array, which the caller rebuilds each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, feedTopicKey])

  const candidates = data?.contentFeedBoostCandidates ?? []

  // notifyOnNetworkStatusChange (wanted, so a filter change shows a spinner
  // rather than a stale list) also flips `loading` during fetchMore. Rendering
  // the loading row INSTEAD of the list then unmounted every row mid-page,
  // collapsing the ScrollView's content height and resetting the scroll to the
  // top. Keep the list mounted while paginating; only a fresh load replaces it.
  const isPaginating = networkStatus === NetworkStatus.fetchMore
  const isReloading = loading && !isPaginating

  const canLoadMore =
    candidates.length > 0 && candidates.length % CANDIDATE_PAGE_SIZE === 0

  // No total on the query, so "another page might exist" is inferred from a
  // full page. Worst case is one extra request returning nothing.
  const loadMore = () =>
    fetchMore({
      variables: { offset: candidates.length },
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult
          ? {
              contentFeedBoostCandidates: [
                ...prev.contentFeedBoostCandidates,
                ...fetchMoreResult.contentFeedBoostCandidates
              ]
            }
          : prev
    })
  const addedKeys = currentBoosts.map(b => keyOf(b.itemType, b.itemId))

  const atLimit =
    mode === 'add' &&
    remainingSlots !== undefined &&
    selected.length >= remainingSlots

  const toggle = (candidate: IContentFeedBoostCandidate) => {
    const key = keyOf(candidate.itemType, candidate.itemId)
    if (mode === 'replace') {
      setSelected(current => (current[0] === key ? [] : [key]))
      return
    }
    setSelected(current => {
      if (current.includes(key)) return current.filter(k => k !== key)
      if (atLimit) return current
      return [...current, key]
    })
  }

  const confirm = () => {
    const picked = candidates
      .filter(c => selected.includes(keyOf(c.itemType, c.itemId)))
      .map(c => asBoost(c.itemType, c.itemId, c.item))
    if (!picked.length) return
    onConfirm(picked)
  }

  const subtitle =
    mode === 'replace'
      ? t('contentCuration:select:replaceSubtitle', {
          count: currentBoosts.length
        })
      : // States the limits rather than counting down. The old copy read
        // "N already in this list, room for M more" off remainingSlots, which is
        // fixed when the modal opens -- so it went stale the moment anything was
        // ticked, and looked broken at the cap.
        t('contentCuration:select:addSubtitle', {
          max: maxItems ?? 0,
          min: minItems ?? 0
        })

  return (
    <Modal
      open={open}
      close={close}
      overlayStyle={PANEL_OVERLAY}
      contentStyle={PANEL_CONTENT}
      bodyStyle={PANEL_BODY}
    >
      <Flex flex={1} p={3}>
        <Flex flexDirection="row" alignItems="flex-start">
          <Flex flex={1}>
            <H4>{t('contentCuration:select:title')}</H4>
            <Text styles={{ fontSize: 12 }}>{subtitle}</Text>
            {/* The server lists Across Groups content only (WOR-646). Said
                here so an item a curator expects and cannot find reads as
                "not shared across groups" rather than as a search miss. */}
            <Text styles={{ fontSize: 12, marginTop: 4, marginBottom: 12 }}>
              {t('contentCuration:select:acrossGroupsOnly')}
            </Text>
          </Flex>
          {/* The shared close icon renders above the body container, which
              would put it outside the panel; this one lives inside it. */}
          <TouchableOpacity onPress={close} style={{ padding: 4 }}>
            <DismissIcon size={18} />
          </TouchableOpacity>
        </Flex>

        <Flex style={{ marginBottom: 8 }}>
          {/* marginBottom: 0 overrides the 20px the shared TextInput bakes in.
              Without it the wrapper is 52px tall while the field is 32px, so
              the absolutely positioned clear button centres itself in the
              margin below the input rather than on the field. */}
          <TextInput
            value={searchInput}
            onChangeText={setSearchInput}
            placeholder={t('contentCuration:select:searchPlaceholder')}
            style={{ width: '100%', paddingRight: 34, marginBottom: 0 }}
          />
          {!!searchInput && (
            <ClearButton
              onPress={() => setSearchInput('')}
              accessibilityLabel={t('contentCuration:select:clearSearch')}
            >
              <ClearIcon size={16} color={theme.colors.subduedIcon} />
            </ClearButton>
          )}
        </Flex>
        <Flex flexDirection="row" alignItems="center" flexWrap="wrap" mb={2}>
          {FORMATS.map(option => (
            <Flex key={option.key} mr={2} mb={1}>
              <SmallButton
                label={t(`contentCuration:select:format:${option.key}`)}
                onPress={() => setFormat(option.key)}
                variant={format === option.key ? 'primary' : 'outline'}
              />
            </Flex>
          ))}
          <Flex flex={1} style={{ minWidth: 180 }}>
            <Dropdown
              options={topicOptions}
              value={topicIds}
              onSelect={next => setTopicIds(next as string[])}
              placeholder={t('contentCuration:select:anyTopic')}
              isMulti
              shortMultiSelect
              closeMenuOnSelect={false}
              style={{ width: '100%', marginBottom: 0 }}
            />
          </Flex>
        </Flex>

        {/* flex: 1, not a fixed maxHeight -- the list should use whatever the
            viewport gives it now that the panel is full height. */}
        <ScrollView style={{ flex: 1 }}>
          {/* Already-curated items first. The server omits them from
              candidates, so without this a curator cannot tell whether an item
              is missing or simply already in the feed. */}
          {currentBoosts.map(boost => (
            <SelectRow
              key={keyOf(boost.itemType, boost.itemId)}
              item={boost.item}
              // Checked, not empty: these items ARE in the feed. An unchecked
              // disabled box reads as "you may not pick this", when what it
              // means is "already selected".
              checked
              disabled
              right={
                <Text styles={{ fontSize: 12 }}>
                  {t('contentCuration:select:added')}
                </Text>
              }
            />
          ))}

          {!isReloading &&
            !error &&
            candidates
              .filter(c => !addedKeys.includes(keyOf(c.itemType, c.itemId)))
              .map(candidate => {
                const key = keyOf(candidate.itemType, candidate.itemId)
                const isChecked = selected.includes(key)
                return (
                  <SelectRow
                    key={key}
                    item={candidate.item}
                    checked={isChecked}
                    disabled={!isChecked && atLimit}
                    onPress={() => toggle(candidate)}
                    right={
                      <>
                        {!!candidate.matchedTopic && (
                          <Text styles={{ fontSize: 12 }}>
                            {candidate.matchedTopic.name}
                          </Text>
                        )}
                        {/* "Featured only": the item carries none of this
                            feed's topics, so it appears while featured and
                            disappears when unfeatured rather than falling into
                            the chronological tail. */}
                        {candidate.inFeedTopics === false && (
                          <Text styles={{ fontSize: 12 }}>
                            {t('contentCuration:select:notInTopics')}
                          </Text>
                        )}
                      </>
                    }
                  />
                )
              })}

          {isReloading && (
            <TableRow>
              <TableCol>
                <Text>{t('common:loading')}</Text>
              </TableCol>
            </TableRow>
          )}
          {!!error && (
            <TableRow>
              <TableCol>
                <Text>{t('contentCuration:select:error')}</Text>
              </TableCol>
            </TableRow>
          )}
          {!isReloading && !error && !candidates.length && (
            <TableRow>
              <TableCol>
                <Text>{t('contentCuration:select:empty')}</Text>
              </TableCol>
            </TableRow>
          )}

          {!isReloading && !error && canLoadMore && (
            <Flex alignItems="center" my={2}>
              {/* Disabled while in flight: fetchMore appends, so a second click
                  would append the same page twice. */}
              <SmallButton
                label={
                  isPaginating
                    ? t('common:loading')
                    : t('contentCuration:select:loadMore')
                }
                onPress={loadMore}
                disabled={isPaginating}
              />
            </Flex>
          )}
        </ScrollView>

        <Flex flexDirection="row" alignItems="center" mt={3}>
          <Text styles={{ fontSize: 12 }}>
            {t('contentCuration:select:selected', { count: selected.length })}
          </Text>
          <Flex flex={1} />
          <Flex mr={2}>
            <SmallButton
              label={t('contentCuration:create:cancel')}
              onPress={close}
            />
          </Flex>
          <SmallButton
            label={
              mode === 'replace'
                ? t('contentCuration:select:replace')
                : t('contentCuration:select:add')
            }
            onPress={confirm}
            disabled={!selected.length || saving}
            variant="primary"
          />
        </Flex>
      </Flex>
    </Modal>
  )
}

export default SelectContentModal
